import { Component, OnDestroy, computed, input, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

/**
 * The single click-to-copy text badge used across the app (resource, office and
 * matter detail pages, the assignment dialogs, and anywhere else a value needs a
 * copy affordance). Renders the value in a badge (or, with `variant="chip"`, a
 * rounded pill); a copy icon is revealed on hover, and clicking writes `value`
 * to the clipboard and briefly swaps the icon to a green checkmark.
 *
 * Copies the raw `value`; set `displayText` when the visible label differs from
 * what should land on the clipboard (e.g. a "ID: " / "#" prefix). Falls back to
 * `document.execCommand('copy')` when the async Clipboard API is unavailable.
 */
@Component({
  selector: 'app-copyable-text',
  standalone: true,
  imports: [MatIconModule],
  templateUrl: './copyable-text.component.html',
  styleUrls: ['./copyable-text.component.scss'],
})
export class CopyableTextComponent implements OnDestroy {
  /** Raw text written to the clipboard on click. */
  readonly value = input.required<string>();
  /** Visible label; defaults to `value` when the display matches what is copied. */
  readonly displayText = input<string>();
  /** Native title / hover tooltip on the badge. */
  readonly copyTitle = input<string>('Click to copy');
  /** Visual treatment: 'badge' (grey monospace ID pill) or 'chip' (rounded pill). */
  readonly variant = input<'badge' | 'chip'>('badge');
  /** Uppercase the label with letter-spacing (e.g. employee/office IDs). Badge variant only. */
  readonly uppercase = input<boolean>(false);

  protected readonly copied = signal(false);
  /**
   * After a successful copy, suppress the hover-reveal of the copy icon until the
   * pointer actually leaves the badge — otherwise the copy icon reappears the moment
   * the checkmark fades while the pointer is still resting on the badge.
   */
  protected readonly suppressed = signal(false);
  protected readonly label = computed(() => this.displayText() ?? this.value());

  private resetTimer?: ReturnType<typeof setTimeout>;

  copy(): void {
    const text = this.value();
    if (!text) return;

    navigator.clipboard.writeText(text).then(
      () => this.showCopied(),
      () => this.fallbackCopy(text),
    );
  }

  private showCopied(): void {
    this.copied.set(true);
    this.suppressed.set(true);
    clearTimeout(this.resetTimer);
    this.resetTimer = setTimeout(() => this.copied.set(false), 2000);
  }

  /** Re-arm the hover-reveal once the pointer leaves the badge. */
  protected onPointerLeave(): void {
    this.suppressed.set(false);
  }

  /** Fallback for browsers where the async Clipboard API rejects (e.g. non-secure contexts). */
  private fallbackCopy(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      this.showCopied();
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }

    document.body.removeChild(textArea);
  }

  ngOnDestroy(): void {
    clearTimeout(this.resetTimer);
  }
}
